import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { styled } from "@mui/system";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useReportsData from "../hooks/useReportsData";
import { getAllActivityType } from "../../loanactivity/Service";

const StyledDatePicker = styled(DatePicker)({
    minWidth: "200px !important",
    "& .MuiOutlinedInput-root": {
        height: "40px",
        "& input": {
            padding: "10px 14px !important",
            fontSize: "14px",
        },
    },
    "& button": { padding: "6px" },
    "& label": { fontSize: "14px" },
});

const MonthlyActivityReport = forwardRef(({
    onGenerate,
    beginDate,
    setBeginDate,
    endDate,
    setEndDate,
    loanFrom,
    setLoanFrom,
    loanTo,
    setLoanTo,
    reportType,
    setReportType,
    sortOption,
    setSortOption,
    selectedActivityType,
    setSelectedActivityType,
    tabValue,
    setTabValue,
    includeNonCashActivity,
    setIncludeNonCashActivity,
    selectedFunds,
    setSelectedFunds,
    selectedCounty,
    setSelectedCounty,
    selectedCity,
    setSelectedCity,
    selectedStatus,
    setSelectedStatus,
    selectedLoanOfficer,
    setSelectedLoanOfficer,
    selectedLoanType,
    setSelectedLoanType,
    selectedBorrower,
    setSelectedBorrower
}, ref) => {
    // Activity type
    const [activityTypes, setActivityTypes] = useState([]);

    // Get common filter data from hook
    const {
        loannums,
        citys,
        loanofficers,
        borrowerTypeList,
        countys,
        loanTypes,
        loanStatusList,
        fundSources,
    } = useReportsData();

    // Fetch activity types on component mount
    useEffect(() => {
        const fetchActivityTypes = async () => {
            try {
                const response = await getAllActivityType();
                console.log('Activity Types API Response:', response);

                // Handle different response structures
                let apiActivityTypes = [];
                if (response.data) {
                    // Could be response.data directly or response.data.activityTypes
                    apiActivityTypes = Array.isArray(response.data)
                        ? response.data
                        : (response.data.activityTypes || []);
                }

                console.log('Parsed Activity Types:', apiActivityTypes);

                // Add 'All' as the first option along with API values
                const activityTypeOptions = [
                    { id: 0, activityTypeName: 'All' },
                    ...apiActivityTypes
                ];

                setActivityTypes(activityTypeOptions);
            } catch (error) {
                console.error('Failed to fetch activity types', error);
                // Set default 'All' option even if API fails
                setActivityTypes([{ id: 0, activityTypeName: 'All' }]);
            }
        };

        fetchActivityTypes();
    }, []);

    // Set default loan range when loans are loaded
    // useEffect(() => {
    //     if (loannums.length > 0 && !loanFrom) {
    //         setLoanFrom(loannums[0].id);
    //         setLoanTo(loannums[loannums.length - 1].id);
    //     }
    // }, [loannums, loanFrom]);

    // Handle list selection change
    const handleListSelectionChange = (value, list, setList) => {
        if (list.includes(value)) {
            setList(list.filter(item => item !== value));
        } else {
            setList([...list, value]);
        }
    };

    // Tab configuration
    const tabPanels = [
        {
            label: 'Funds',
            options: fundSources.map(fs => fs.code + ' ' + fs.description),
            selected: selectedFunds,
            setSelected: setSelectedFunds
        },
        {
            label: 'County',
            options: countys.map(c => c.county),
            selected: selectedCounty,
            setSelected: setSelectedCounty
        },
        {
            label: 'City',
            options: citys.map(c => c.city),
            selected: selectedCity,
            setSelected: setSelectedCity
        },
        {
            label: 'Status',
            options: loanStatusList.map(ls => ls.statusCode),
            selected: selectedStatus,
            setSelected: setSelectedStatus
        },
        {
            label: 'Loan Officer',
            options: loanofficers.map(lo => lo.loanOfficer),
            selected: selectedLoanOfficer,
            setSelected: setSelectedLoanOfficer
        },
        {
            label: 'Loan Type',
            options: loanTypes.map(lt => lt.code + ' ' + lt.description),
            selected: selectedLoanType,
            setSelected: setSelectedLoanType
        },
        {
            label: 'Borrower',
            options: borrowerTypeList.map(b => b.code + ' ' + b.description),
            selected: selectedBorrower,
            setSelected: setSelectedBorrower
        },
    ];

    const handleGenerate = () => {
        const payload = {
            reportName: 'Monthly Activity Report',
            beginDate: beginDate || null,
            endDate: endDate || null,
            loanFrom: loanFrom ? parseInt(loannums.find(x => x.id === loanFrom)?.loanNumber ?? "0") : 0,
            loanTo: loanTo ? parseInt(loannums.find(x => x.id === loanTo)?.loanNumber ?? "0") : 0,
            reportType,
            sortOption,
            activityType: selectedActivityType,
            selectedFunds,
            selectedCounty,
            selectedCity,
            selectedStatus,
            selectedLoanOfficer,
            selectedLoanType,
            selectedBorrower,
            includeNonCashActivity,
        };

        console.log('Monthly Activity Report Payload:', payload);

        if (onGenerate) {
            onGenerate(payload);
        }
    };

    // Expose handleGenerate to parent component
    useImperativeHandle(ref, () => ({
        generateReport: handleGenerate
    }));

    return (
        <Box>
            {/* Report Type - Moved to top */}
            <Box display="flex" gap={2} mb={2}>
                <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                        value={reportType}
                        onChange={e => setReportType(e.target.value)}
                        label="Report Type"
                    >
                        <MenuItem value="Detail">Detail</MenuItem>
                        <MenuItem value="Summary">Summary</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Date Fields */}
            <Box display="flex" gap={2} mb={2}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StyledDatePicker
                        label="Begin Date"
                        value={beginDate ? dayjs(beginDate) : null}
                        onChange={(newValue) => setBeginDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                        slotProps={{
                            textField: {
                                size: "small",
                                fullWidth: true,
                            }
                        }}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StyledDatePicker
                        label="End Date"
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(newValue) => setEndDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                        slotProps={{
                            textField: {
                                size: "small",
                                fullWidth: true,
                            }
                        }}
                    />
                </LocalizationProvider>
            </Box>

            {/* Show remaining fields only when Report Type is Detail */}
            {reportType === 'Detail' && (
                <>
                    {/* Loan Range */}
                    <Box display="flex" gap={2} mb={2}>
                        <FormControl fullWidth>
                            <InputLabel>Loan From</InputLabel>
                            <Select
                                value={loanFrom}
                                onChange={e => setLoanFrom(e.target.value)}
                                label="Loan From"
                            >
                                <MenuItem value="">
                                    <em>Select Loan</em>
                                </MenuItem>
                                {loannums.map((loannum) => (
                                    <MenuItem key={loannum.id} value={loannum.id}>
                                        {loannum.loanNumber} - {loannum.loanName?.trim() || '(No Name)'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Loan To</InputLabel>
                            <Select
                                value={loanTo}
                                onChange={e => setLoanTo(e.target.value)}
                                label="Loan To"
                            >
                                <MenuItem value="">
                                    <em>Select Loan</em>
                                </MenuItem>
                                {loannums.map((loannum) => (
                                    <MenuItem key={loannum.id} value={loannum.id}>
                                        {loannum.loanNumber} - {loannum.loanName?.trim() || '(No Name)'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Activity Type */}
                    <Box display="flex" gap={2} mb={2}>
                        <FormControl fullWidth>
                            <InputLabel>Activity Type</InputLabel>
                            <Select
                                value={selectedActivityType}
                                onChange={e => setSelectedActivityType(e.target.value)}
                                label="Activity Type"
                            >
                                {activityTypes.map((activityType) => (
                                    <MenuItem key={activityType.id} value={activityType.activityTypeName || activityType.name || 'All'}>
                                        {activityType.activityTypeName || activityType.name || 'All'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Sort Options */}
                    <Box display="flex" gap={2} mb={2}>
                        <FormControl fullWidth>
                            <InputLabel>Sort Options</InputLabel>
                            <Select
                                value={sortOption}
                                onChange={e => setSortOption(e.target.value)}
                                label="Sort Options"
                            >
                                <MenuItem value="# Loan Number"># Loan Number</MenuItem>
                                <MenuItem value="Date">Date</MenuItem>
                                <MenuItem value="Fund">Fund</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Filter Tabs */}
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        {tabPanels.map((tab, idx) => (
                            <Tab key={idx} label={tab.label} />
                        ))}
                    </Tabs>

                    <Box
                        mt={2}
                        sx={{
                            maxHeight: 300,
                            overflowY: 'auto',
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            p: 1
                        }}
                    >
                        <List>
                            {tabPanels[tabValue].options.map((option, index) => (
                                <ListItem
                                    key={index}
                                    button
                                    onClick={() =>
                                        handleListSelectionChange(
                                            option,
                                            tabPanels[tabValue].selected,
                                            tabPanels[tabValue].setSelected
                                        )
                                    }
                                    selected={tabPanels[tabValue].selected.includes(option)}
                                >
                                    <ListItemText
                                        primary={option}
                                        secondary={
                                            tabPanels[tabValue].selected.includes(option)
                                                ? 'Selected'
                                                : ''
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    {/* Include Non-Cash Activity Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeNonCashActivity}
                                onChange={(e) => setIncludeNonCashActivity(e.target.checked)}
                            />
                        }
                        label="Include Non-Cash Activity"
                    />
                </>
            )}
        </Box>
    );
});

export default MonthlyActivityReport;
